import { Stack, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useContext, useEffect, useState } from "react";
import "../../App.css";
import { StyleConstants, Styles } from "../../Styles";
import RestfulManager, { RES_STATUS_OK } from "../../manager/RestfulManager";
import SnackbarContext from "../../components/SnackbarContext";
import WifiIcon from "@mui/icons-material/Wifi";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import BoltIcon from "@mui/icons-material/Bolt";
import dayjs from "dayjs";
import {
  getSqlDateByDatePicker,
  getSqlDateTimeByDateTimePicker,
  getSqlDate,
} from "../../utils/DateUtils";
import { jsonToQueryString } from "../../utils/StringUtils";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import fileDownload from "js-file-download";
import { NavLink } from "react-router-dom";
import SessionManager from "../../manager/SessionManager";
import { ARCHIVED_DAYS } from "../../config/Config";

const SettingsPage = () => {
  const { setOpenSnackBar, setSnackbarSeverity, setSnackbarMessage } =
    useContext(SnackbarContext);

  const sessionManager = new SessionManager();
  const restfulManager = new RestfulManager();

  const today = new Date();

  const minArchivedDateDate = new Date("2024-11-27");
  const minArchivedDate = dayjs(getSqlDate(minArchivedDateDate));

  const archiveLastDate = new Date();
  archiveLastDate.setDate(today.getDate() - ARCHIVED_DAYS);
  const maxArchivedDateDate = archiveLastDate;
  const maxArchivedDate = dayjs(getSqlDate(maxArchivedDateDate));

  const minStartDateDate = maxArchivedDateDate;
  minStartDateDate.setDate(minStartDateDate.getDate() + 1);
  const minStartDate = dayjs(getSqlDate(minStartDateDate));

  const defaultStartDate = dayjs(getSqlDate(today) + "T00:00");
  const defaultEndDate = dayjs(getSqlDate(today) + "T23:59");

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [archivedDate, setArchivedDate] = useState(maxArchivedDate);

  const [cars, setCars] = useState([]);

  const [rawPageCarCode, setRawPageCarCode] = useState(
    sessionManager.getRawPageCarCode() !== null
      ? sessionManager.getRawPageCarCode()
      : "",
  );
  const [exportCarCode, setExportCarCode] = useState("");
  const [exportArchivedCarCode, setExportArchivedCarCode] = useState("");

  const rawPageCarSelectChange = (event) => {
    const selectedCarCode = event.target.value;
    setRawPageCarCode(selectedCarCode);
    sessionManager.setRawPageCarCode(selectedCarCode);
  };

  const exportCarSelectChange = (event) => {
    const selectedCarCode = event.target.value;
    setExportCarCode(selectedCarCode);
  };

  const exportArchivedCarSelectChange = (event) => {
    const selectedCarCode = event.target.value;
    setExportArchivedCarCode(selectedCarCode);
  };

  const exportCsv = () => {
    const payload = {
      startDate: startDate ? getSqlDateTimeByDateTimePicker(startDate) : "",
      endDate: endDate ? getSqlDateTimeByDateTimePicker(endDate) : "",
      carCode: exportCarCode,
    };

    if (payload.startDate.length === 0) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Please choose the start date and time.");
      setOpenSnackBar(true);
      return;
    }

    if (payload.endDate.length === 0) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Please choose the end date and time.");
      setOpenSnackBar(true);
      return;
    }

    if (payload.carCode.length === 0) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Please choose a Car No..");
      setOpenSnackBar(true);
      return;
    }

    const query = jsonToQueryString(payload);

    (async () => {
      try {
        const res = await restfulManager.getPacketCsv(
          sessionManager.getAccessToken(),
          query,
        );
        const json = await res.json();
        if (res.status === 200 && json.status === RES_STATUS_OK) {
          fileDownload(json.fileContent, "packet.csv");
        } else {
          if (json && json.msg) {
            setSnackbarSeverity("error");
            setSnackbarMessage(json.msg);
            setOpenSnackBar(true);
          }
        }
      } catch (e) {
        console.error(e);
        setSnackbarSeverity("error");
        setSnackbarMessage("Error. Please contact administrator.");
        setOpenSnackBar(true);
      }
    })();
  };

  const exportArchivedCsv = () => {
    const payload = {
      archivedDate: archivedDate ? getSqlDateByDatePicker(archivedDate) : "",
      carCode: exportArchivedCarCode,
    };

    if (payload.archivedDate.length === 0) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Please choose the date.");
      setOpenSnackBar(true);
      return;
    }

    if (payload.carCode.length === 0) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Please choose a Car No..");
      setOpenSnackBar(true);
      return;
    }

    const query = jsonToQueryString(payload);

    (async () => {
      try {
        const res = await restfulManager.getPacketArchivedCsv(
          sessionManager.getAccessToken(),
          query,
        );
        if (res.status === 200) {
          const blob = await res.blob();
          const filename =
            getSqlDateByDatePicker(archivedDate) +
            "-" +
            exportArchivedCarCode +
            ".csv";
          fileDownload(blob, filename);
        } else {
          const json = await res.json();
          if (json && json.msg) {
            setSnackbarSeverity("error");
            setSnackbarMessage(json.msg);
            setOpenSnackBar(true);
          }
        }
      } catch (e) {
        console.error(e);
        setSnackbarSeverity("error");
        setSnackbarMessage("Error. Please contact administrator.");
        setOpenSnackBar(true);
      }
    })();
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await restfulManager.getCars(
          sessionManager.getAccessToken(),
        );
        const json = await res.json();
        if (res.status === 200 && json.status === RES_STATUS_OK) {
          const carsArray = json.cars;
          carsArray.unshift({ carCode: "" });
          setCars(carsArray);

          const defaultCar = carsArray[0];
          setExportCarCode(defaultCar.carCode);
          setExportArchivedCarCode(defaultCar.carCode);
        }
      } catch (e) {
        console.error(e);
        setSnackbarSeverity("error");
        setSnackbarMessage("Error. Please contact administrator.");
        setOpenSnackBar(true);
      }
    })();
  }, []);

  return (
    <>
      <Box sx={{ flexGrow: 1, padding: "8px" }}>
        <Stack direction="row" sx={{ width: "100%", height: "100%" }}>
          <Stack direction="column" sx={{ width: "28%", alignItems: "center" }}>
            <WifiIcon style={{ ...Styles.bigIcon }} />

            <Stack direction="row" sx={{ alignItems: "center" }}>
              <Typography
                sx={{
                  fontSize: Styles.contentFont3,
                  color: StyleConstants.fontColor,
                  textAlign: "left",
                  marginRight: "8px",
                }}
              >
                Car Number:
              </Typography>
              <Select
                value={rawPageCarCode}
                onChange={rawPageCarSelectChange}
                sx={Styles.carSelect}
                MenuProps={{
                  sx: {
                    "& .MuiMenuItem-root.Mui-selected": {
                      color: "red",
                      backgroundColor: StyleConstants.mainBgColor,
                    },
                    "& .MuiMenuItem-root:hover": {
                      backgroundColor: StyleConstants.highlightBgColor,
                    },
                    "& .MuiMenuItem-root.Mui-selected:hover": {
                      backgroundColor: StyleConstants.highlightBgColor,
                    },
                  },
                }}
              >
                {cars.map((car) => (
                  <MenuItem
                    key={car.carCode}
                    value={car.carCode}
                    sx={Styles.carSelect}
                  >
                    <Typography sx={{ fontSize: Styles.contentFont4 }}>
                      {car.carCode}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </Stack>

            <NavLink to="/raw">
              <Button
                variant="contained"
                sx={{ marginTop: "16px", width: "320px" }}
              >
                Raw Data
              </Button>
            </NavLink>
          </Stack>

          <Stack direction="column" sx={{ width: "44%", alignItems: "center" }}>
            <FileDownloadIcon style={{ ...Styles.bigIcon }} />
            <Stack direction="row" sx={{ width: "100%" }}>
              <Stack direction="column" sx={{ width: "50%", padding: "8px" }}>
                <Stack direction="row" sx={{ alignItems: "center" }}>
                  <Typography
                    sx={{
                      fontSize: Styles.contentFont3,
                      color: StyleConstants.fontColor,
                      textAlign: "left",
                      marginRight: "8px",
                    }}
                  >
                    Car Number:
                  </Typography>
                  <Select
                    value={exportCarCode}
                    onChange={exportCarSelectChange}
                    sx={Styles.carSelect}
                    MenuProps={{
                      sx: {
                        "& .MuiMenuItem-root.Mui-selected": {
                          color: "red",
                          backgroundColor: StyleConstants.mainBgColor,
                        },
                        "& .MuiMenuItem-root:hover": {
                          backgroundColor: StyleConstants.highlightBgColor,
                        },
                        "& .MuiMenuItem-root.Mui-selected:hover": {
                          backgroundColor: StyleConstants.highlightBgColor,
                        },
                      },
                    }}
                  >
                    {cars.map((car) => (
                      <MenuItem
                        key={car.carCode}
                        value={car.carCode}
                        sx={Styles.carSelect}
                      >
                        <Typography sx={{ fontSize: Styles.contentFont4 }}>
                          {car.carCode}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
                <Stack
                  direction="column"
                  sx={{ width: "100%", height: 200, alignItems: "center" }}
                >
                  <Typography
                    sx={{
                      fontSize: Styles.contentFont3,
                      color: StyleConstants.fontColor,
                      alignItems: "center",
                      textAlign: "center",
                      marginTop: "16px",
                    }}
                  >
                    Date
                  </Typography>
                  <Stack
                    direction="row"
                    sx={{ width: "100%", alignItems: "center" }}
                  >
                    <Typography
                      sx={{
                        width: "100px",
                        fontSize: Styles.contentFont3,
                        color: StyleConstants.fontColor,
                        textAlign: "left",
                      }}
                    >
                      From:
                    </Typography>
                    <DateTimePicker
                      views={["year", "month", "day", "hours", "minutes"]}
                      sx={{
                        "& .MuiInputBase-input": {
                          color: StyleConstants.fontColor,
                          fontSize: Styles.contentFont3,
                        },
                        "& .MuiSvgIcon-root": {
                          fill: StyleConstants.fontColor,
                        },
                      }}
                      onChange={(newValue) => {
                        setStartDate(newValue);
                      }}
                      defaultValue={defaultStartDate}
                      minDate={minStartDate}
                      timeSteps={{ minutes: 1 }}
                      ampm={false}
                    />
                  </Stack>
                  <Stack direction="row" sx={{ alignItems: "center" }}>
                    <Typography
                      sx={{
                        width: "100px",
                        fontSize: Styles.contentFont3,
                        color: StyleConstants.fontColor,
                        textAlign: "left",
                      }}
                    >
                      To:
                    </Typography>
                    <DateTimePicker
                      views={["year", "month", "day", "hours", "minutes"]}
                      sx={{
                        "& .MuiInputBase-input": {
                          color: StyleConstants.fontColor,
                          fontSize: Styles.contentFont3,
                        },
                        "& .MuiSvgIcon-root": {
                          fill: StyleConstants.fontColor,
                        },
                      }}
                      onChange={(newValue) => {
                        setEndDate(newValue);
                      }}
                      defaultValue={defaultEndDate}
                      minDate={minStartDate}
                      timeSteps={{ minutes: 1 }}
                      ampm={false}
                    />
                  </Stack>
                </Stack>
                <Button variant="contained" onClick={exportCsv}>
                  Export CSV
                </Button>
                <Typography
                  sx={{
                    fontSize: Styles.contentFont1,
                    color: StyleConstants.fontColor,
                    textAlign: "left",
                  }}
                >
                  Data from more than 30 days ago has been archived. For
                  accessing archived data, please use the "Export Archived CSV".
                </Typography>
              </Stack>
              <Stack direction="column" sx={{ width: "50%", padding: "8px" }}>
                <Stack direction="row" sx={{ alignItems: "center" }}>
                  <Typography
                    sx={{
                      fontSize: Styles.contentFont3,
                      color: StyleConstants.fontColor,
                      textAlign: "left",
                      marginRight: "8px",
                    }}
                  >
                    Car Number:
                  </Typography>
                  <Select
                    value={exportArchivedCarCode}
                    onChange={exportArchivedCarSelectChange}
                    sx={Styles.carSelect}
                    MenuProps={{
                      sx: {
                        "& .MuiMenuItem-root.Mui-selected": {
                          color: "red",
                          backgroundColor: StyleConstants.mainBgColor,
                        },
                        "& .MuiMenuItem-root:hover": {
                          backgroundColor: StyleConstants.highlightBgColor,
                        },
                        "& .MuiMenuItem-root.Mui-selected:hover": {
                          backgroundColor: StyleConstants.highlightBgColor,
                        },
                      },
                    }}
                  >
                    {cars.map((car) => (
                      <MenuItem
                        key={car.carCode}
                        value={car.carCode}
                        sx={Styles.carSelect}
                      >
                        <Typography sx={{ fontSize: Styles.contentFont4 }}>
                          {car.carCode}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
                <Stack
                  direction="column"
                  sx={{ width: "100%", height: 200, alignItems: "center" }}
                >
                  <Typography
                    sx={{
                      width: "100px",
                      fontSize: Styles.contentFont3,
                      color: StyleConstants.fontColor,
                      textAlign: "left",
                      marginTop: "16px",
                    }}
                  >
                    Date
                  </Typography>
                  <DatePicker
                    sx={{
                      "& .MuiInputBase-input": {
                        color: StyleConstants.fontColor,
                        fontSize: Styles.contentFont3,
                      },
                      "& .MuiSvgIcon-root": {
                        fill: StyleConstants.fontColor,
                      },
                    }}
                    onChange={(newValue) => {
                      setArchivedDate(newValue);
                    }}
                    defaultValue={maxArchivedDate}
                    minDate={minArchivedDate}
                    maxDate={maxArchivedDate}
                  />
                </Stack>
                <Button variant="contained" onClick={exportArchivedCsv}>
                  Export Archived CSV
                </Button>
              </Stack>
            </Stack>
          </Stack>

          <Stack direction="column" sx={{ width: "28%", alignItems: "center" }}>
            <BoltIcon style={{ ...Styles.bigIcon }} />

            <NavLink to="/car-settings">
              <Button variant="contained">Energy Consumption</Button>
            </NavLink>
          </Stack>
        </Stack>
      </Box>
    </>
  );
};

export default SettingsPage;
